import React, { useState, useEffect, useRef } from 'react';
import HxSelect from '../../../components/ui/HxSelect';
import { UserCheck as LUserCheck } from 'lucide-react';
import { FiClock, FiTrash2, FiX, FiMapPin, FiAlignLeft, FiAlertTriangle, FiCheck, FiUser, FiUserCheck, FiMaximize2, FiMinimize2, FiFileText, FiExternalLink, FiVideo, FiSidebar, FiSquare, FiCopy, FiCalendar } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { updateCourse } from '../services';
import { buildEventDescription, DEFAULT_TEMPLATE, TEMPLATE_VARS } from '../utils/eventDescription';

// Enlace del evento en Google Calendar "Hingenia org" (lo escribe la sincronización
// automática de masterclass). Es el que el asesor pega en el grupo de WhatsApp.
const GcalLinkBar = ({ event }) => {
    const link = event.gcalHtmlLink || event.resource?.gcalHtmlLink;
    const isMC = event.type === 'masterclass' || event.resource?.type === 'masterclass';
    if (!isMC) return null;
    if (!link) {
        return (
            <div className="flex items-center gap-2 text-[11px] text-gray-400 px-1">
                <FiCalendar size={11} /> Aún no está en Google Calendar (se sincroniza en unos minutos)
            </div>
        );
    }
    return (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
            <FiCalendar size={13} className="text-emerald-600 shrink-0" />
            <span className="text-[11px] font-semibold text-emerald-800 flex-1 min-w-0 truncate">En Google Calendar · Hingenia org</span>
            <a href={link} target="_blank" rel="noopener noreferrer" className="text-[11px] text-emerald-700 hover:underline shrink-0">Abrir</a>
            <button onClick={() => { navigator.clipboard.writeText(link); toast.success('Enlace copiado para WhatsApp'); }}
                className="flex items-center gap-1 text-[11px] font-semibold bg-emerald-600 text-white px-2.5 py-1 rounded-lg hover:bg-emerald-700 shrink-0">
                <FiCopy size={11} /> Copiar para WhatsApp
            </button>
        </div>
    );
};

const COLORS = [
    '#3b82f6', // Blue
    '#ef4444', // Red
    '#10b981', // Green
    '#f59e0b', // Yellow
    '#8b5cf6', // Violet
    '#ec4899', // Pink
    '#6366f1', // Indigo
    '#6b7280'  // Gray
];

// Local date string helper (avoids UTC timezone shift)
const toLocalDateStr = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
};

const EventPopover = ({ event, onClose, onSave, onDelete, onCancel, position, instructors = [], courses = [], asesores = [], variant = 'popup', onToggleVariant, descriptionTemplate = '', onSaveTemplate, classInfo = null }) => {
    const isCancelled = event.status === 'cancelled';
    const instructor = instructors.find(i => i.id === event.instructorId || i.id === event.resource?.instructorId);
    const course = courses.find(c => c.id === event.courseId || c.id === event.resource?.courseId);
    const [title, setTitle] = useState(event.title);
    const [color, setColor] = useState(event.color || '#3b82f6');
    const [eventDate, setEventDate] = useState(toLocalDateStr(event.start));
    const [showReprogram, setShowReprogram] = useState(false);
    const [reprDate, setReprDate] = useState('');
    const [reprStart, setReprStart] = useState('18:00');
    const [reprEnd, setReprEnd] = useState('20:00');
    const [description, setDescription] = useState('');
    // Plantilla activa (la del usuario o la de por defecto) y editor inline.
    const template = descriptionTemplate || DEFAULT_TEMPLATE;
    const [editingTemplate, setEditingTemplate] = useState(false);
    const [templateDraft, setTemplateDraft] = useState(template);
    // Última descripción auto-generada: mientras la descripción visible coincida
    // con este valor, se considera "no tocada" y se refresca sola cuando cambian
    // hora, enlace de Zoom o título. Si el usuario la edita a mano, deja de seguir
    // los cambios automáticos (para no pisar notas propias).
    const autoDescRef = useRef('');
    const copyDescription = () => {
        navigator.clipboard.writeText(description || '')
            .then(() => toast.success('Descripción copiada'))
            .catch(() => toast.error('No se pudo copiar'));
    };
    const handleSaveTemplate = async () => {
        if (onSaveTemplate) {
            try { await onSaveTemplate(templateDraft); } catch { /* el padre ya avisa */ }
        }
        setEditingTemplate(false);
        const fresh = buildEventDescription(buildLiveEvent(), course, instructor, templateDraft, classInfo);
        setDescription(fresh);
        autoDescRef.current = fresh;
    };
    const [expanded, setExpanded] = useState(false);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [claseLiveUrl, setClaseLiveUrl] = useState(
        event.claseLiveUrl || course?.claseVariables?.claseLiveUrl || ''
    );
    const [claseMeetingId, setClaseMeetingId] = useState(
        event.claseMeetingId || course?.claseVariables?.claseMeetingId || ''
    );
    const [clasePassword, setClasePassword] = useState(
        event.clasePassword || course?.claseVariables?.clasePassword || ''
    );
    const [asesorId, setAsesorId] = useState(event.asesorId || '');
    const isMC = event.type === 'masterclass' || event.resource?.type === 'masterclass';
    const selectedAsesor = asesores.find(a => a.id === asesorId);

    // Helper: genera slots de tiempo cada 30 min en formato 12h
    const timeSlots = [];
    for (let h = 5; h <= 23; h++) {
        [0, 30].forEach(m => {
            const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
            const ampm = h >= 12 ? 'pm' : 'am';
            const h12 = h % 12 || 12;
            timeSlots.push({ val, label: `${h12}:${String(m).padStart(2, '0')} ${ampm}` });
        });
    }

    // Evento "vivo": el mismo evento pero con los campos que se editan en el
    // popover (título, fecha/hora, enlace de Zoom) al día, para que la
    // descripción siempre refleje lo que se ve en pantalla y no datos viejos.
    const buildLiveEvent = () => {
        const baseDate = eventDate || toLocalDateStr(event.start);
        let start = event.start;
        let end = event.end;
        if (startTime) {
            const [h, m] = startTime.split(':').map(Number);
            start = new Date(baseDate + 'T00:00:00');
            start.setHours(h, m, 0, 0);
        }
        if (endTime) {
            const [h, m] = endTime.split(':').map(Number);
            end = new Date(baseDate + 'T00:00:00');
            end.setHours(h, m, 0, 0);
        }
        return { ...event, title, start, end, claseLiveUrl, claseMeetingId, clasePassword };
    };

    // Al abrir el popover (o cambiar de evento): recarga los campos editables
    // y regenera la descripción SIEMPRE a partir de los datos actuales del
    // evento/curso — nunca se conserva un texto guardado desactualizado.
    useEffect(() => {
        setTitle(event.title);
        setColor(event.color || '#3b82f6');
        setEventDate(toLocalDateStr(event.start));
        setEditingTemplate(false);
        setExpanded(false);
        setClaseLiveUrl(event.claseLiveUrl || course?.claseVariables?.claseLiveUrl || '');
        setClaseMeetingId(event.claseMeetingId || course?.claseVariables?.claseMeetingId || '');
        setClasePassword(event.clasePassword || course?.claseVariables?.clasePassword || '');
        setAsesorId(event.asesorId || '');
        const s = event.start instanceof Date ? event.start : new Date(event.start);
        const e = event.end instanceof Date ? event.end : new Date(event.end);
        setStartTime(`${String(s.getHours()).padStart(2, '0')}:${String(s.getMinutes()).padStart(2, '0')}`);
        setEndTime(`${String(e.getHours()).padStart(2, '0')}:${String(e.getMinutes()).padStart(2, '0')}`);
        const fresh = buildEventDescription(event, course, instructor, template, classInfo);
        setDescription(fresh);
        autoDescRef.current = fresh;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event]);

    // Mientras el usuario no haya tocado la descripción a mano, la mantiene
    // sincronizada con hora, título y enlace/ID/contraseña de Zoom en vivo
    // (sin necesidad de un botón "Regenerar").
    useEffect(() => {
        const fresh = buildEventDescription(buildLiveEvent(), course, instructor, template, classInfo);
        setDescription(prev => (prev === autoDescRef.current ? fresh : prev));
        autoDescRef.current = fresh;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, eventDate, startTime, endTime, claseLiveUrl, claseMeetingId, clasePassword]);

    const handleSave = async () => {
        const updates = { title, color, notes: description, claseLiveUrl, claseMeetingId, clasePassword, asesorId: asesorId || null, asesorName: selectedAsesor?.displayName || selectedAsesor?.name || null };
        // Build new start/end dates using eventDate + time
        const baseDate = eventDate || toLocalDateStr(event.start);
        if (startTime) {
            const [h, m] = startTime.split(':').map(Number);
            const newStart = new Date(baseDate + 'T00:00:00');
            newStart.setHours(h, m, 0, 0);
            updates.start = newStart;
        }
        if (endTime) {
            const [h, m] = endTime.split(':').map(Number);
            const newEnd = new Date(baseDate + 'T00:00:00');
            newEnd.setHours(h, m, 0, 0);
            updates.end = newEnd;
        }
        onSave(event.id, updates);

        // Sincronizar al curso (Gestor): si es CL y hay link, actualiza claseVariables.
        const courseId = event.courseId || event.resource?.courseId;
        if (event.type === 'clase' && courseId && claseLiveUrl) {
            try {
                await updateCourse(courseId, {
                    claseVariables: {
                        ...(course?.claseVariables || {}),
                        claseLiveUrl,
                        claseMeetingId,
                        clasePassword,
                    },
                });
            } catch (err) {
                console.error('Error sincronizando claseVariables del curso', err);
            }
        }
    };

    const style = {
        top: Math.max(10, Math.min(position.y, window.innerHeight - 520)),
        left: Math.max(10, Math.min(position.x, window.innerWidth - 440))
    };

    const zoomAcctName = event.zoomAccountName
        || (event.type === 'masterclass' ? course?.defaultVariables?._zoomAccountName : course?.claseVariables?._claseAccountName)
        || course?.defaultVariables?._zoomAccountName;

    // Read-only view for Google Calendar events
    if (event.source === 'google_calendar') {
        return (
            <div className="fixed z-50 bg-white dark:bg-[#1e1e2e] rounded-xl shadow-2xl border border-blue-200 dark:border-[#45475a] w-80 animate-fadeIn" style={style}>
                <div className="flex justify-end p-2 bg-blue-50 rounded-t-xl border-b border-blue-100">
                    <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-200 rounded-full transition" title="Cerrar">
                        <FiX />
                    </button>
                </div>
                <div className="p-4 space-y-3">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
                        <h3 className="text-lg font-semibold text-gray-800">{event.title}</h3>
                    </div>
                    <div className="flex gap-3 items-center text-sm text-gray-600">
                        <FiClock className="text-gray-400" />
                        <span>
                            {event.start.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                            <br />
                            {event.allDay ? 'Todo el día' : `${event.start.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} - ${event.end.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`}
                        </span>
                    </div>
                    {event.location && (
                        <div className="flex gap-3 items-center text-sm text-gray-600">
                            <FiMapPin className="text-gray-400" />
                            <span>{event.location}</span>
                        </div>
                    )}
                    {event.description && (
                        <div className="flex gap-3 items-start text-sm text-gray-600">
                            <FiAlignLeft className="text-gray-400 mt-0.5" />
                            <span className="line-clamp-3">{event.description}</span>
                        </div>
                    )}
                    <p className="text-xs text-blue-500 italic">Evento de Google Calendar (solo lectura)</p>
                    <div className="flex gap-2 pt-1">
                        {event.htmlLink && (
                            <a href={event.htmlLink} target="_blank" rel="noopener noreferrer"
                                className="flex-1 text-center text-xs px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium">
                                Abrir en Google Calendar
                            </a>
                        )}
                        <button onClick={onClose}
                            className="text-xs px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition font-medium">
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (expanded) {
        return (
            <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
                <div className="bg-white dark:bg-[#1e1e2e] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                    {/* Expanded Header */}
                    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100" style={{ backgroundColor: `${color}10` }}>
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                            <div className="w-3.5 h-3.5 rounded shrink-0" style={{ backgroundColor: color }} />
                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 ${event.type === 'masterclass' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                {event.type === 'masterclass' ? 'Masterclass' : 'Clase'}
                            </span>
                            {course && <span className="text-sm text-gray-500 truncate">{course.name}</span>}
                            {zoomAcctName && (
                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200 shrink-0">
                                    {zoomAcctName}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-0.5 shrink-0">
                            <button onClick={() => setExpanded(false)} className="p-1.5 text-gray-400 hover:bg-gray-200 rounded-lg" title="Minimizar"><FiMinimize2 size={15} /></button>
                            <button onClick={() => onDelete(event.id)} className="p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 rounded-lg" title="Eliminar"><FiTrash2 size={15} /></button>
                            <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-200 rounded-lg" title="Cerrar"><FiX size={15} /></button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-5 space-y-4">
                        {/* Title */}
                        <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                            className="w-full text-xl font-bold text-gray-900 dark:text-gray-100 border-none outline-none placeholder-gray-300 dark:placeholder-gray-600 bg-transparent"
                            placeholder="Título del evento" />

                        {/* Time + Instructor row */}
                        <div className="flex items-center gap-6 flex-wrap">
                            <div className="flex items-center gap-2.5 text-sm text-gray-600">
                                <FiClock className="text-gray-400 shrink-0" size={16} />
                                <span className="font-medium">{event.start.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                <select value={startTime} onChange={e => setStartTime(e.target.value)}
                                    className="px-2 py-1 border border-gray-200 dark:border-[#45475a] rounded-lg text-xs bg-gray-50 dark:bg-[#313244] dark:text-gray-200 focus:ring-1 focus:ring-blue-400 outline-none cursor-pointer appearance-none">
                                    {timeSlots.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
                                </select>
                                <span className="text-gray-400 text-xs">-</span>
                                <select value={endTime} onChange={e => setEndTime(e.target.value)}
                                    className="px-2 py-1 border border-gray-200 dark:border-[#45475a] rounded-lg text-xs bg-gray-50 dark:bg-[#313244] dark:text-gray-200 focus:ring-1 focus:ring-blue-400 outline-none cursor-pointer appearance-none">
                                    {timeSlots.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
                                </select>
                            </div>
                            {instructor && (
                                <div className="flex items-center gap-2.5">
                                    {instructor.photoURL ? (
                                        <img src={instructor.photoURL} alt="" className="w-8 h-8 rounded-full object-cover" />
                                    ) : (
                                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: instructor.color || '#3b82f6' }}>
                                            {instructor.name?.charAt(0)?.toUpperCase()}
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-sm font-medium text-gray-800 leading-tight">{instructor.name}</p>
                                        {instructor.email && <p className="text-[11px] text-gray-400">{instructor.email}</p>}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Asesor selector — solo MC (expanded) */}
                        {isMC && asesores.length > 0 && (
                            <div className="flex items-center gap-3">
                                <FiUserCheck className="text-gray-400 shrink-0" size={16} />
                                <HxSelect
                                    className="full"
                                    minWidth={0}
                                    value={asesorId}
                                    onChange={setAsesorId}
                                    options={[
                                        { value: '', label: 'Sin asesor asignado', icon: LUserCheck },
                                        ...[...asesores].sort((a, b) => (a.displayName || a.name || a.email || '').localeCompare(b.displayName || b.name || b.email || '')).map(a => ({
                                            value: a.id, label: a.displayName || a.name || a.email, sub: a.email || undefined, photo: a.photoURL || undefined, color: a.color || '#0e9f6e',
                                        })),
                                    ]}
                                    placeholder="Sin asesor asignado"
                                    searchPlaceholder="Buscar asesor…"
                                    icon={LUserCheck}
                                    ariaLabel="Asesor"
                                />
                                {selectedAsesor && (
                                    <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 shrink-0">
                                        {(selectedAsesor.displayName || selectedAsesor.name || '').split(' ')[0]}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Zoom links — Masterclass: prefiere el link a nivel evento (asignado por
                            distribución de cuentas Zoom) sobre el default del curso. */}
                        {event.type !== 'clase' && (() => {
                            const mcUrl = event.mcLiveUrl || course?.defaultVariables?.zoomLink || '';
                            const mcId = event.mcMeetingId || course?.defaultVariables?.meetingId || '';
                            const mcPass = event.mcPassword || course?.defaultVariables?.password || '';
                            const wa = course?.defaultVariables?.whatsappLink || '';
                            if (!mcUrl && !wa) return null;
                            return (
                                <div className="bg-blue-50/60 rounded-lg p-3 space-y-1.5">
                                    {mcUrl && (
                                        <div className="flex items-center gap-2">
                                            <FiVideo size={12} className="text-blue-600 shrink-0" />
                                            <a href={mcUrl} target="_blank" rel="noopener noreferrer"
                                                className="text-xs text-blue-600 hover:underline truncate flex-1">
                                                {mcUrl}
                                            </a>
                                            <button onClick={() => { navigator.clipboard.writeText(mcUrl); toast.success('Copiado'); }}
                                                className="text-[10px] text-blue-500 hover:text-blue-700 px-1.5 py-0.5 bg-blue-100 rounded shrink-0">Copiar</button>
                                        </div>
                                    )}
                                    {mcId && (
                                        <p className="text-xs text-gray-500 ml-5">ID: <b>{mcId}</b> &nbsp; Pass: <b>{mcPass || '-'}</b></p>
                                    )}
                                    {wa && (
                                        <div className="flex items-center gap-2 ml-5">
                                            <a href={wa} target="_blank" rel="noopener noreferrer"
                                                className="text-xs text-green-600 hover:underline truncate">
                                                WhatsApp
                                            </a>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Zoom links — Clase */}
                        {event.type === 'clase' && (
                            <div className="bg-blue-50/60 rounded-lg p-3 space-y-2">
                                <p className="text-[11px] font-bold text-blue-700 flex items-center gap-1.5">
                                    <FiVideo size={12} /> Enlace clase en vivo
                                </p>
                                <div className="flex items-center gap-2">
                                    <input type="url" value={claseLiveUrl} onChange={e => setClaseLiveUrl(e.target.value)}
                                        placeholder="https://zoom.us/j/..."
                                        className="flex-1 text-xs px-2.5 py-1.5 border border-blue-200 rounded-lg focus:ring-1 focus:ring-blue-400 outline-none bg-white" />
                                    {claseLiveUrl && (
                                        <a href={claseLiveUrl} target="_blank" rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 shrink-0"><FiExternalLink size={13} /></a>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <input type="text" value={claseMeetingId} onChange={e => setClaseMeetingId(e.target.value)}
                                        placeholder="ID reunión"
                                        className="text-xs px-2.5 py-1.5 border border-blue-200 rounded-lg focus:ring-1 focus:ring-blue-400 outline-none bg-white" />
                                    <input type="text" value={clasePassword} onChange={e => setClasePassword(e.target.value)}
                                        placeholder="Contraseña"
                                        className="text-xs px-2.5 py-1.5 border border-blue-200 rounded-lg focus:ring-1 focus:ring-blue-400 outline-none bg-white" />
                                </div>
                            </div>
                        )}

                        <GcalLinkBar event={event} />

                        {/* Description — amplified */}
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Descripción</span>
                                <div className="flex items-center gap-3">
                                    <button onClick={copyDescription}
                                        className="flex items-center gap-1 text-[11px] font-semibold text-gray-500 hover:text-gray-700 transition"
                                        title="Copiar descripción">
                                        <FiCopy size={11} /> Copiar
                                    </button>
                                </div>
                            </div>
                            <textarea value={description} onChange={e => setDescription(e.target.value)}
                                placeholder="Agrega notas, materiales, instrucciones adicionales..."
                                rows={6}
                                className="w-full px-4 py-3 border border-gray-200 dark:border-[#45475a] rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none resize-y bg-gray-50/50 dark:bg-[#313244] dark:text-gray-200 leading-relaxed" />
                        </div>
                    </div>

                    {/* Footer — colors inline + save */}
                    <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                            {COLORS.map(c => (
                                <button key={c} onClick={() => setColor(c)}
                                    className={`w-5 h-5 rounded-full border-2 transition ${color === c ? 'border-gray-600 scale-110' : 'border-transparent hover:scale-105'}`}
                                    style={{ backgroundColor: c }} />
                            ))}
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                            <button onClick={onClose} className="px-4 py-1.5 text-gray-500 hover:bg-gray-100 rounded-lg text-sm font-medium">Cancelar</button>
                            <button onClick={handleSave} className="px-5 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center gap-1.5 shadow-sm">
                                <FiCheck size={14} /> Guardar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const popoverBody = (
        <>
            {/* Header — colored accent + badges + actions */}
            <div className="flex items-center justify-between px-3.5 py-2 rounded-t-2xl border-b border-gray-100 dark:border-[#313244] shrink-0" style={{ backgroundColor: `${color}12` }}>
                <div className="flex items-center gap-2 min-w-0">
                    <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: color }} />
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${event.type === 'masterclass' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {event.type === 'masterclass' ? 'Masterclass' : 'Clase'}
                    </span>
                    {zoomAcctName && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200 shrink-0">
                            {zoomAcctName}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                    {onToggleVariant && (
                        <button onClick={onToggleVariant}
                            className="p-1.5 text-gray-400 hover:bg-indigo-50 hover:text-indigo-600 rounded-full transition"
                            title={variant === 'column' ? 'Ver como ventana flotante' : 'Abrir como columna a la derecha'}>
                            {variant === 'column' ? <FiSquare size={13} /> : <FiSidebar size={13} />}
                        </button>
                    )}
                    <button onClick={() => setExpanded(true)} className="p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-600 rounded-full transition" title="Vista expandida">
                        <FiMaximize2 size={13} />
                    </button>
                    {!isCancelled && onCancel && (
                        <button onClick={() => onCancel(event.id)}
                            className="p-1.5 text-gray-400 hover:bg-orange-50 hover:text-orange-600 rounded-full transition" title="Cancelar clase">
                            <FiAlertTriangle size={13} />
                        </button>
                    )}
                    <button onClick={() => onDelete(event.id)} className="p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 rounded-full transition" title="Eliminar">
                        <FiTrash2 size={13} />
                    </button>
                    <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-200 rounded-full transition" title="Cerrar">
                        <FiX size={13} />
                    </button>
                </div>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
                {/* Cancelled banner */}
                {isCancelled && (
                    <div className="mx-3.5 mt-2.5 space-y-1.5">
                        <div className="px-2.5 py-1.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                            <span className="text-red-600 text-[10px] font-bold">CANCELADA</span>
                            <span className="flex-1" />
                            {onCancel && (
                                <button onClick={() => onCancel(event.id, true)} className="text-[9px] px-2 py-0.5 bg-green-100 text-green-700 rounded font-bold hover:bg-green-200">
                                    Reactivar
                                </button>
                            )}
                        </div>
                        {onSave && (showReprogram ? (
                            <div className="px-2.5 py-2 bg-blue-50 border border-blue-200 rounded-lg space-y-1.5">
                                <p className="text-[10px] font-bold text-blue-700">Reprogramar sesión</p>
                                <input type="date" value={reprDate} onChange={e => setReprDate(e.target.value)}
                                    className="w-full text-xs px-2 py-1 border border-blue-200 rounded bg-white" />
                                <div className="flex gap-2">
                                    <input type="time" value={reprStart} onChange={e => setReprStart(e.target.value)}
                                        className="flex-1 text-xs px-2 py-1 border border-blue-200 rounded bg-white font-mono" />
                                    <input type="time" value={reprEnd} onChange={e => setReprEnd(e.target.value)}
                                        className="flex-1 text-xs px-2 py-1 border border-blue-200 rounded bg-white font-mono" />
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => setShowReprogram(false)} className="flex-1 text-[10px] py-1 border border-gray-200 rounded text-gray-600 hover:bg-gray-50">Cancelar</button>
                                    <button disabled={!reprDate} onClick={() => {
                                        const newStart = new Date(`${reprDate}T${reprStart}:00`);
                                        const newEnd = new Date(`${reprDate}T${reprEnd}:00`);
                                        onSave(event.id, { start: newStart, end: newEnd, status: 'confirmed', cancelledAt: null });
                                        toast.success('Sesión reprogramada');
                                        onClose();
                                    }} className="flex-1 text-[10px] py-1 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 disabled:opacity-40">
                                        Reprogramar
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button onClick={() => setShowReprogram(true)}
                                className="w-full text-[10px] py-1.5 bg-blue-50 text-blue-600 border border-blue-200 rounded-lg font-bold hover:bg-blue-100 flex items-center justify-center gap-1">
                                <FiClock size={10} /> Reprogramar fecha
                            </button>
                        ))}
                    </div>
                )}

                <div className={`p-4 space-y-3 ${variant === 'column' ? 'flex flex-col min-h-full' : ''}`}>
                    {/* Title */}
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full text-[15px] font-bold text-gray-900 dark:text-gray-100 border-b-2 border-transparent focus:border-blue-500 outline-none placeholder-gray-300 dark:placeholder-gray-600 bg-transparent pb-1"
                        placeholder="Añade un título"
                        autoFocus
                    />

                    {/* Date + Time — two rows for clarity */}
                    <div className="flex items-center gap-2.5">
                        <FiClock className="text-gray-400 shrink-0" size={14} />
                        <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)}
                            className="px-2 py-1 border border-gray-200 dark:border-[#45475a] rounded-lg text-xs bg-gray-50 dark:bg-[#313244] dark:text-gray-200 focus:ring-1 focus:ring-blue-400 outline-none cursor-pointer w-[130px]" />
                        <div className="flex items-center gap-1.5">
                            <select value={startTime} onChange={e => setStartTime(e.target.value)}
                                className="px-2 py-1 border border-gray-200 dark:border-[#45475a] rounded-lg text-xs bg-gray-50 dark:bg-[#313244] dark:text-gray-200 focus:ring-1 focus:ring-blue-400 outline-none cursor-pointer appearance-none">
                                {timeSlots.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
                            </select>
                            <span className="text-gray-400 text-xs">-</span>
                            <select value={endTime} onChange={e => setEndTime(e.target.value)}
                                className="px-2 py-1 border border-gray-200 dark:border-[#45475a] rounded-lg text-xs bg-gray-50 dark:bg-[#313244] dark:text-gray-200 focus:ring-1 focus:ring-blue-400 outline-none cursor-pointer appearance-none">
                                {timeSlots.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Course name */}
                    {course && (
                        <p className="text-[13px] text-gray-600 dark:text-gray-400 leading-snug">{course.name}</p>
                    )}

                    {/* Instructor */}
                    {instructor && (
                        <div className="flex items-center gap-2.5">
                            {instructor.photoURL ? (
                                <img src={instructor.photoURL} alt="" className="w-7 h-7 rounded-full object-cover" />
                            ) : (
                                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: instructor.color || '#3b82f6' }}>
                                    {instructor.name?.charAt(0)?.toUpperCase()}
                                </div>
                            )}
                            <p className="text-[13px] font-medium text-gray-700 dark:text-gray-300">{instructor.name}</p>
                        </div>
                    )}

                    {/* Asesor selector — solo MC */}
                    {isMC && asesores.length > 0 && (
                        <div className="flex items-center gap-2">
                            <FiUserCheck className="text-gray-400 shrink-0" size={14} />
                            <HxSelect
                                className="full"
                                minWidth={0}
                                value={asesorId}
                                onChange={setAsesorId}
                                options={[
                                        { value: '', label: 'Sin asesor', icon: LUserCheck },
                                        ...[...asesores].sort((a, b) => (a.displayName || a.name || a.email || '').localeCompare(b.displayName || b.name || b.email || '')).map(a => ({
                                            value: a.id, label: a.displayName || a.name || a.email, sub: a.email || undefined, photo: a.photoURL || undefined, color: a.color || '#0e9f6e',
                                        })),
                                    ]}
                                placeholder="Sin asesor"
                                searchPlaceholder="Buscar asesor…"
                                icon={LUserCheck}
                                ariaLabel="Asesor"
                            />
                            {selectedAsesor && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 shrink-0">
                                    {(selectedAsesor.displayName || selectedAsesor.name || '').split(' ')[0]}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Zoom links — Masterclass (prefiere link asignado por distribución) */}
                    {event.type !== 'clase' && (() => {
                        const mcUrl = event.mcLiveUrl || course?.defaultVariables?.zoomLink || '';
                        const mcId = event.mcMeetingId || course?.defaultVariables?.meetingId || '';
                        const mcPass = event.mcPassword || course?.defaultVariables?.password || '';
                        if (!mcUrl) return null;
                        return (
                            <div className="bg-blue-50/60 rounded-lg p-2.5 space-y-1">
                                <a href={mcUrl} target="_blank" rel="noopener noreferrer"
                                    className="text-[11px] text-blue-600 hover:underline flex items-center gap-1.5 break-all">
                                    <FiExternalLink size={11} className="shrink-0" /> {mcUrl}
                                </a>
                                {mcId && (
                                    <p className="text-[11px] text-gray-500">ID: {mcId} &nbsp; Pass: {mcPass || '-'}</p>
                                )}
                            </div>
                        );
                    })()}

                    {/* Zoom links — Clase */}
                    {event.type === 'clase' && (
                        <div className="bg-blue-50/60 rounded-lg p-2.5 space-y-2">
                            <p className="text-[11px] font-bold text-blue-700 flex items-center gap-1.5">
                                <FiVideo size={11} /> Enlace clase en vivo
                            </p>
                            <input type="url" value={claseLiveUrl} onChange={e => setClaseLiveUrl(e.target.value)}
                                placeholder="https://zoom.us/j/..."
                                className="w-full text-xs px-2.5 py-1.5 border border-blue-200 rounded-lg focus:ring-1 focus:ring-blue-400 outline-none bg-white" />
                            <div className="grid grid-cols-2 gap-2">
                                <input type="text" value={claseMeetingId} onChange={e => setClaseMeetingId(e.target.value)}
                                    placeholder="ID reunión"
                                    className="min-w-0 text-xs px-2 py-1.5 border border-blue-200 rounded-lg focus:ring-1 focus:ring-blue-400 outline-none bg-white" />
                                <input type="text" value={clasePassword} onChange={e => setClasePassword(e.target.value)}
                                    placeholder="Contraseña"
                                    className="min-w-0 text-xs px-2 py-1.5 border border-blue-200 rounded-lg focus:ring-1 focus:ring-blue-400 outline-none bg-white" />
                            </div>
                        </div>
                    )}

                    <GcalLinkBar event={event} />

                    {/* Description */}
                    <div className={`space-y-1 ${variant === 'column' ? 'flex-1 flex flex-col min-h-0' : ''}`}>
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">Descripción</span>
                            <div className="flex items-center gap-2.5">
                                <button onClick={copyDescription}
                                    className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 hover:text-gray-700 transition"
                                    title="Copiar descripción">
                                    <FiCopy size={10} /> Copiar
                                </button>
                                <button onClick={() => { setTemplateDraft(template); setEditingTemplate(v => !v); }}
                                    className={`flex items-center gap-1 text-[10px] font-semibold transition ${editingTemplate ? 'text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                                    title="Editar mi plantilla de descripción">
                                    <FiFileText size={10} /> Plantilla
                                </button>
                            </div>
                        </div>

                        {editingTemplate ? (
                            <div className="space-y-2 border border-indigo-200 dark:border-indigo-500/40 rounded-lg p-2.5 bg-indigo-50/50 dark:bg-indigo-500/10">
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-relaxed">
                                    Variables disponibles:{' '}
                                    {TEMPLATE_VARS.map(v => (
                                        <code key={v} className="inline-block bg-white dark:bg-[#313244] border border-gray-200 dark:border-[#45475a] rounded px-1 mx-0.5 text-[9px] text-indigo-600 dark:text-indigo-300">{`{${v}}`}</code>
                                    ))}
                                </p>
                                <textarea value={templateDraft} onChange={e => setTemplateDraft(e.target.value)}
                                    rows={variant === 'column' ? 10 : 7}
                                    className="w-full text-[12px] font-mono px-2.5 py-2 border border-indigo-200 dark:border-[#45475a] rounded-lg focus:ring-1 focus:ring-indigo-400 outline-none resize-y bg-white dark:bg-[#1e1e2e] dark:text-gray-200 leading-relaxed" />
                                <div className="flex items-center justify-end gap-2">
                                    <button onClick={() => { setTemplateDraft(DEFAULT_TEMPLATE); }}
                                        className="text-[10px] px-2 py-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-[#313244] rounded transition mr-auto">
                                        Restablecer
                                    </button>
                                    <button onClick={() => setEditingTemplate(false)}
                                        className="text-[10px] px-2.5 py-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-[#313244] rounded transition">
                                        Cancelar
                                    </button>
                                    <button onClick={handleSaveTemplate}
                                        className="text-[10px] px-3 py-1 bg-indigo-600 text-white rounded font-semibold hover:bg-indigo-700 transition flex items-center gap-1">
                                        <FiCheck size={11} /> Guardar plantilla
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <textarea value={description} onChange={e => setDescription(e.target.value)}
                                placeholder="Agregar descripción..."
                                rows={variant === 'column' ? 8 : 4}
                                className={`w-full text-[13px] px-3 py-2 border border-gray-200 dark:border-[#45475a] rounded-lg focus:ring-1 focus:ring-blue-400 outline-none resize-y bg-gray-50/50 dark:bg-[#313244] dark:text-gray-200 leading-relaxed ${variant === 'column' ? 'flex-1 min-h-[160px]' : ''}`} />
                        )}
                    </div>
                </div>
            </div>

            {/* Footer — colors + save */}
            <div className="px-4 py-2.5 border-t border-gray-100 dark:border-[#313244] shrink-0 flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                    {COLORS.map(c => (
                        <button key={c} onClick={() => setColor(c)}
                            className={`w-5 h-5 rounded-full border-2 transition ${color === c ? 'border-gray-600 scale-110' : 'border-transparent hover:scale-105'}`}
                            style={{ backgroundColor: c }} />
                    ))}
                </div>
                <button onClick={handleSave}
                    className="ml-auto bg-blue-600 text-white px-5 py-2 rounded-lg text-xs font-semibold hover:bg-blue-700 transition shadow-sm flex items-center gap-1.5">
                    <FiCheck size={13} /> Guardar
                </button>
            </div>
        </>
    );

    // Modo columna: el contenedor (ancho + redimensionado) lo provee CalendarPage,
    // aquí solo llenamos el panel.
    if (variant === 'column') {
        return (
            <div className="flex flex-col h-full w-full bg-white dark:bg-[#1e1e2e] overflow-hidden">
                {popoverBody}
            </div>
        );
    }

    // Modo popup (por defecto): tarjeta flotante posicionada en el clic.
    return (
        <div
            className="fixed z-50 bg-white dark:bg-[#1e1e2e] rounded-2xl shadow-2xl border border-gray-200/60 dark:border-[#45475a] w-[420px] animate-fadeIn max-h-[88vh] flex flex-col overflow-hidden"
            style={style}
        >
            {popoverBody}
        </div>
    );
};

export default EventPopover;
